import PresenceService from "./presence.service";

const STORAGE_KEY = "scio_presence_session_id";
const DISABLED_PRESENCE_VALUES = new Set(["false", "0", "no", "off", "disabled"]);

function isPresenceEnabled() {
  const raw = import.meta.env.VITE_ENABLE_USER_PRESENCE ?? import.meta.env.VITE_ENABLE_SCENARIO_PRESENCE;
  if (raw === undefined || raw === null || String(raw).trim() === "") return true;

  return !DISABLED_PRESENCE_VALUES.has(String(raw).trim().toLowerCase());
}

function presenceUrl(path) {
  const useLocalApiProxy = String(import.meta.env.VITE_USE_LOCAL_API ?? '').toLowerCase() === 'true';
  const base = useLocalApiProxy ? "" : String(import.meta.env.VITE_SCIO_API_URL || "").replace(/\/+$/, "");
  return `${base}/api/v2/usr/presence/${path}`;
}

class PresenceManager {
  constructor() {
    this.sessionId = null;
    this.timer = null;
    this.running = false;
    this.intervalMs = 60_000;
    this.lastBeatAt = 0;
    this._hooksAttached = false;
  }

  getSessionId() {
    return this.sessionId;
  }

  isEnabled() {
    return isPresenceEnabled();
  }

  resetLocalState() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.running = false;
    this.sessionId = null;
    sessionStorage.removeItem(STORAGE_KEY);
  }

  async heartbeatNow({ getCurrentProjectId, getCurrentRoute } = {}) {
    if (!this.isEnabled()) return false;
    return await this._safeBeat({ getCurrentProjectId, getCurrentRoute });
  }

  async start({ getCurrentProjectId, getCurrentRoute, intervalMs } = {}) {
    if (!this.isEnabled()) {
      this.resetLocalState();
      return false;
    }

    if (this.running && this.sessionId) return true;

    if (intervalMs && Number.isFinite(intervalMs)) this.intervalMs = intervalMs;

    const existing = sessionStorage.getItem(STORAGE_KEY);
    if (existing) {
      this.sessionId = existing;
      this.running = true;
      this._attachLifecycleHooks({ getCurrentProjectId, getCurrentRoute });
      this._startTimer({ getCurrentProjectId, getCurrentRoute });
      await this._safeBeat({ getCurrentProjectId, getCurrentRoute });
      return true;
    }

    const resp = await PresenceService.startSession({
      currentProjectId: getCurrentProjectId?.() ?? null,
      currentRoute: getCurrentRoute?.() ?? null,
    });

    const sessionId = resp?.sessionId ?? resp?.data?.sessionId;
    if (!sessionId) {
      this.resetLocalState();
      return false;
    }

    this.sessionId = sessionId;
    sessionStorage.setItem(STORAGE_KEY, sessionId);

    this.running = true;
    this._attachLifecycleHooks({ getCurrentProjectId, getCurrentRoute });
    this._startTimer({ getCurrentProjectId, getCurrentRoute });

    await this._safeBeat({ getCurrentProjectId, getCurrentRoute });
    return true;
  }

  async stop() {
    if (!this.isEnabled()) {
      this.resetLocalState();
      return;
    }

    if (!this.running && !this.sessionId) return;

    const sid = this.sessionId;
    this.resetLocalState();

    try {
      await PresenceService.endSession({ sessionId: sid });
    } catch {
      // Session expiry is non-critical for normal application use.
    }
  }

  _startTimer({ getCurrentProjectId, getCurrentRoute }) {
    if (this.timer) clearInterval(this.timer);

    this.timer = setInterval(() => {
      const now = Date.now();
      if (now - this.lastBeatAt < 10_000) return;

      this._safeBeat({ getCurrentProjectId, getCurrentRoute });
    }, this.intervalMs);
  }

  async _safeBeat({ getCurrentProjectId, getCurrentRoute }) {
    if (!this.isEnabled()) return false;
    if (!this.running || !this.sessionId) return false;

    this.lastBeatAt = Date.now();

    try {
      const result = await PresenceService.heartbeat({
        sessionId: this.sessionId,
        currentProjectId: getCurrentProjectId?.() ?? null,
        currentRoute: getCurrentRoute?.() ?? null,
      });

      if (result?.sessionMissing) {
        return await this._restart({ getCurrentProjectId, getCurrentRoute });
      }

      if (result?.disabled) {
        this.resetLocalState();
        return false;
      }

      return result?.ok !== false;
    } catch {
      try {
        return await this._restart({ getCurrentProjectId, getCurrentRoute });
      } catch {
        return false;
      }
    }
  }

  async _restart({ getCurrentProjectId, getCurrentRoute }) {
    if (!this.isEnabled()) return false;

    sessionStorage.removeItem(STORAGE_KEY);
    this.sessionId = null;

    const resp = await PresenceService.startSession({
      currentProjectId: getCurrentProjectId?.() ?? null,
      currentRoute: getCurrentRoute?.() ?? null,
    });

    const sessionId = resp?.sessionId ?? resp?.data?.sessionId;
    if (!sessionId) return false;

    this.sessionId = sessionId;
    this.running = true;
    sessionStorage.setItem(STORAGE_KEY, sessionId);
    return true;
  }

  _attachLifecycleHooks({ getCurrentProjectId, getCurrentRoute }) {
    if (this._hooksAttached) return;
    this._hooksAttached = true;

    document.addEventListener("visibilitychange", () => {
      if (!this.running) return;
      if (document.visibilityState === "visible") {
        this._safeBeat({ getCurrentProjectId, getCurrentRoute });
      }
    });

    const endOnExit = () => this.endSessionOnPageExit();
    window.addEventListener("pagehide", endOnExit);
    window.addEventListener("beforeunload", endOnExit);
  }

  endSessionOnPageExit() {
    const sid = this.sessionId;
    if (!sid) return;

    const body = JSON.stringify({ sessionId: sid });
    const url = presenceUrl("end");

    try {
      if (navigator.sendBeacon) {
        const payload = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(url, payload);
      } else {
        fetch(url, {
          method: "POST",
          body,
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          keepalive: true,
        });
      }
    } catch {
      // A stale session naturally falls offline after the server-side freshness window.
    } finally {
      this.resetLocalState();
    }
  }
}

export default new PresenceManager();
